import { useState } from "react";
import { useLocation } from "wouter";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Building2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import { useRequestOtp, useVerifyOtp, getGetCurrentUserQueryKey } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";

const phoneSchema = z.object({
  whatsappNumber: z.string().min(10, "Phone number must be at least 10 digits"),
});

export default function Login() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<"phone" | "otp">("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpValue, setOtpValue] = useState("");

  const requestOtp = useRequestOtp();
  const verifyOtp = useVerifyOtp();

  const phoneForm = useForm<z.infer<typeof phoneSchema>>({
    resolver: zodResolver(phoneSchema),
    defaultValues: { whatsappNumber: "" },
  });

  const onPhoneSubmit = (data: z.infer<typeof phoneSchema>) => {
    requestOtp.mutate({ data }, {
      onSuccess: () => {
        setPhoneNumber(data.whatsappNumber);
        setStep("otp");
        toast({ title: "OTP Sent", description: "Please check your WhatsApp" });
      },
      onError: (error) => {
        toast({ variant: "destructive", title: "Error", description: error.message || "Failed to send OTP" });
      }
    });
  };

  const onOtpSubmit = (otp: string) => {
    verifyOtp.mutate({ data: { whatsappNumber: phoneNumber, otp } }, {
      onSuccess: (response) => {
        localStorage.setItem("crm_token", response.token);
        queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() });
        toast({ title: "Success", description: "Logged in successfully" });
        setLocation("/");
      },
      onError: (error) => {
        toast({ variant: "destructive", title: "Error", description: error.message || "Invalid OTP" });
      }
    });
  };

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col justify-center items-center p-4">
      <div className="w-full max-w-md flex flex-col items-center mb-8">
        <div className="bg-primary/10 p-3 rounded-xl mb-4">
          <Building2 className="w-10 h-10 text-primary" />
        </div>
        <h1 className="text-3xl font-serif text-foreground">Coworking Hub</h1>
        <p className="text-muted-foreground mt-2">Workspace Management System</p>
      </div>

      <Card className="w-full max-w-md shadow-lg border-muted">
        <CardHeader>
          <CardTitle>{step === "phone" ? "Sign In" : "Enter Verification Code"}</CardTitle>
          <CardDescription>
            {step === "phone" 
              ? "Enter your WhatsApp number to receive an OTP."
              : `We sent a code to ${phoneNumber}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === "phone" ? (
            <Form {...phoneForm}>
              <form onSubmit={phoneForm.handleSubmit(onPhoneSubmit)} className="space-y-4">
                <FormField
                  control={phoneForm.control}
                  name="whatsappNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>WhatsApp Number</FormLabel>
                      <FormControl>
                        <Input placeholder="9999999999" {...field} />
                      </FormControl>
                      <p className="text-xs text-muted-foreground pt-1">We'll send a 6-digit code on WhatsApp.</p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" disabled={requestOtp.isPending}>
                  {requestOtp.isPending ? "Sending..." : "Send OTP"}
                </Button>
              </form>
            </Form>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (otpValue.length !== 6) {
                  toast({ variant: "destructive", title: "Invalid OTP", description: "Enter the 6-digit code" });
                  return;
                }
                onOtpSubmit(otpValue);
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <label htmlFor="otp-input" className="text-sm font-medium">One-Time Password</label>
                <input
                  id="otp-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  placeholder="123456"
                  value={otpValue}
                  onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-center text-lg tracking-[0.5em] font-mono shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
                <p className="text-xs text-muted-foreground">The code expires in 5 minutes.</p>
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" className="w-full" onClick={() => { setOtpValue(""); setStep("phone"); }}>
                  Back
                </Button>
                <Button type="submit" className="w-full" disabled={verifyOtp.isPending}>
                  {verifyOtp.isPending ? "Verifying..." : "Verify"}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
